import { BarChart3, Check } from "lucide-react";
import { useState } from "react";
import { FeatureHintUmfrage } from "./useFeatureHints";

/**
 * Die Umfrage unter einer Neuigkeit (KAN-302).
 *
 * Der Kunde gibt seine Antwort ab, mehr nicht: Ein Ergebnis bekommt er nicht zu sehen -
 * es wird ihm nicht einmal geschickt. Was dabei herauskommt, erzaehlen wir spaeter selbst
 * ueber eine Neuigkeit.
 *
 * Unter der Frage steht, ob die Antwort anonym ausgewertet wird. Wer nicht weiss, ob sein
 * Name daranhaengt, antwortet entweder zu vorsichtig oder faehrt ungewollt mit offenem
 * Visier - beides vermeidet dieser eine Satz.
 *
 * Dieselbe Flaeche steht im Neuigkeiten-Bereich und im Fenster beim Anmelden: Beide zeigen
 * dieselbe Ansicht, und die Umfrage gehoert zum Eintrag, nicht zum Ort.
 */
export const UmfrageBlock = ({
  umfrage,
  readOnly,
  onAntwort,
}: {
  umfrage: FeatureHintUmfrage;
  /** Waehrend einer Impersonierung: sichtbar, aber nicht bedienbar. */
  readOnly?: boolean;
  onAntwort: (optionen: number[]) => Promise<boolean>;
}) => {
  const [gewaehlt, setGewaehlt] = useState<number[]>(umfrage.meineAntwort);
  const [gesendet, setGesendet] = useState(umfrage.meineAntwort.length > 0);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(false);

  const waehlen = (stelle: number): void => {
    setFehler(false);
    setGewaehlt(vorher => {
      if (!umfrage.mehrfach) return vorher[0] === stelle ? [] : [stelle];
      return vorher.includes(stelle) ? vorher.filter(eintrag => eintrag !== stelle) : [...vorher, stelle];
    });
  };

  const senden = async (): Promise<void> => {
    if (gewaehlt.length === 0 || laeuft) return;

    setLaeuft(true);
    const geklappt = await onAntwort([...gewaehlt].sort((a, b) => a - b));
    setLaeuft(false);

    // Eine Stimme ist eine Aussage, kein Messwert: Kommt sie nicht an, muss der Kunde das
    // erfahren und es erneut versuchen koennen.
    if (geklappt) setGesendet(true);
    else setFehler(true);
  };

  const gewaehlteLabels = umfrage.optionen
    .filter(option => gewaehlt.includes(option.stelle))
    .map(option => option.label)
    .join(" · ");

  return (
    <div className="mt-1 flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50/60 p-3.5">
      <div className="flex items-center gap-1.5">
        <BarChart3 size={15} strokeWidth={2} className="text-neuigkeit" aria-hidden="true" />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-neuigkeit">Kurze Frage an dich</span>
      </div>

      {gesendet ? (
        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-2.5">
            <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary">
              <Check size={12} strokeWidth={3.2} className="text-white" aria-hidden="true" />
            </span>
            <p className="m-0 text-sm leading-relaxed text-gray-900">Danke, das hilft uns bei der Planung.</p>
          </div>
          {gewaehlteLabels && (
            <p className="m-0 text-xs leading-relaxed text-gray-600">
              Deine Auswahl: <strong className="font-semibold text-gray-900">{gewaehlteLabels}</strong>
            </p>
          )}
          {!readOnly && (
            <button
              type="button"
              onClick={() => setGesendet(false)}
              className="min-h-8 self-start rounded-lg border border-gray-300 px-2.5 text-xs font-medium text-gray-700 hover:bg-white"
            >
              Antwort ändern
            </button>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="m-0 text-sm font-semibold leading-snug text-gray-900">{umfrage.frage}</p>

          <div className="flex flex-col gap-1.5">
            {umfrage.optionen.map(option => {
              const aktiv = gewaehlt.includes(option.stelle);
              return (
                <button
                  key={option.stelle}
                  type="button"
                  disabled={readOnly}
                  aria-pressed={aktiv}
                  onClick={() => waehlen(option.stelle)}
                  className={`flex w-full items-start gap-2.5 rounded-lg border px-2.5 py-2 text-left text-[13px] ${
                    aktiv ? "border-primary bg-primary-hell" : "border-gray-200 bg-white hover:bg-gray-50"
                  } ${readOnly ? "cursor-default opacity-70" : ""}`}
                >
                  <span
                    aria-hidden="true"
                    className={`mt-px flex h-4 w-4 flex-none items-center justify-center border ${
                      umfrage.mehrfach ? "rounded" : "rounded-full"
                    } ${aktiv ? "border-primary bg-primary" : "border-gray-300"}`}
                  >
                    {aktiv && <Check size={10} strokeWidth={3.4} className="text-white" />}
                  </span>
                  <span className="text-gray-900">{option.label}</span>
                </button>
              );
            })}
          </div>

          {!readOnly && (
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={senden}
                disabled={gewaehlt.length === 0 || laeuft}
                className={`min-h-9 rounded-lg px-3 text-[13px] font-semibold text-white ${
                  gewaehlt.length === 0 || laeuft ? "cursor-default bg-gray-300" : "bg-primary hover:opacity-90"
                }`}
              >
                {laeuft ? "Wird gesendet …" : "Antwort senden"}
              </button>
              {gewaehlt.length === 0 && <span className="text-[11px] text-gray-400">{umfrage.mehrfach ? "Mehrere möglich" : "Eine Antwort"}</span>}
            </div>
          )}

          {fehler && (
            <p className="m-0 text-xs leading-relaxed text-red-600" role="alert">
              Deine Antwort konnte nicht gespeichert werden. Bitte versuch es noch einmal.
            </p>
          )}
        </div>
      )}

      <p className="m-0 border-t border-gray-200 pt-2 text-[11px] leading-relaxed text-gray-400">
        {umfrage.nichtAnonym ? "Deine Antwort geht an das kandidat.io-Team." : "Deine Antwort wird anonym ausgewertet."}
      </p>
    </div>
  );
};

export default UmfrageBlock;
